/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import { useEuiTheme } from '../../services';
import classNames from 'classnames';
import { EuiPanel } from '../panel';
import { EuiAvatar } from '../avatar';
import { euiCommentEventStyles, euiCommentEventHeaderStyles, euiCommentEventBodyStyles } from './comment_event.styles';
export var EuiCommentEvent = ({
  children,
  className,
  eventIcon,
  eventIconAriaLabel,
  username,
  timestamp,
  event,
  actions,
  eventColor
}) => {
  const classes = classNames('euiCommentEvent', className);

  // the username is required so we only check if other elements are define
  const hasEventElements = eventIcon || timestamp || event || actions;
  const isTypeRegular = children && hasEventElements;
  const isTypeUpdate = !children && hasEventElements;
  let type;
  if (isTypeRegular) {
    type = 'regular';
  } else if (isTypeUpdate) {
    type = 'update';
  } else {
    type = 'custom';
  }
  const euiTheme = useEuiTheme();
  const styles = euiCommentEventStyles(euiTheme);
  const cssStyles = [styles.euiCommentEvent, styles[type]];
  const headerStyles = euiCommentEventHeaderStyles(euiTheme);
  const cssHeaderStyles = [headerStyles.euiCommentEvent__header, eventColor && headerStyles.hasEventColor, isTypeRegular && headerStyles.regular];
  const bodyStyles = euiCommentEventBodyStyles(euiTheme);
  const cssBodyStyles = [bodyStyles.euiCommentEvent__body, bodyStyles[type]];
  const isFigure = isTypeRegular;
  const Element = isFigure ? 'figure' : 'div';
  const HeaderElement = isFigure ? 'figcaption' : 'div';

  // The 'plain' color creates a shadow and adds a border radius that we don't want.
  // So for these cases we use the transparent color instead.
  const finalEventColor = eventColor === 'plain' ? 'transparent' : eventColor;
  const panelProps = finalEventColor ? {
    color: finalEventColor,
    paddingSize: 's'
  } : {
    color: 'transparent',
    paddingSize: 'none'
  };
  return <Element className={classes} css={cssStyles} data-type={type}>
      {hasEventElements && <HeaderElement className="euiCommentEvent__header" css={cssHeaderStyles}>
          <EuiPanel {...panelProps}>
            <div className="euiCommentEvent__headerMain" css={headerStyles.euiCommentEvent__headerMain}>
              <div className="euiCommentEvent__headerData" css={headerStyles.euiCommentEvent__headerData}>
                {eventIcon && <EuiAvatar className="euiCommentEvent__headerEventIcon" css={headerStyles.euiCommentEvent__headerEventIcon} size="s" iconType={eventIcon} name={eventIconAriaLabel ? eventIconAriaLabel : ''} color="subdued" aria-hidden={!eventIconAriaLabel} />}
                {username && <div className="euiCommentEvent__headerUsername" css={headerStyles.euiCommentEvent__headerUsername}>
                    {username}
                  </div>}
                {event && <div className="euiCommentEvent__headerEvent" css={headerStyles.euiCommentEvent__headerEvent}>
                    {event}
                  </div>}
                {timestamp && <div className="euiCommentEvent__headerTimestamp">
                    <time>{timestamp}</time>
                  </div>}
              </div>
              {actions && <div className="euiCommentEvent__headerActions" css={headerStyles.euiCommentEvent__headerActions}>
                  {actions}
                </div>}
            </div>
          </EuiPanel>
        </HeaderElement>}

      {children && <div className="euiCommentEvent__body" css={cssBodyStyles}>
          {children}
        </div>}
    </Element>;
};
EuiCommentEvent.propTypes = {
  /**
     * Author of the comment.
     */
  username: PropTypes.node.isRequired,
  /**
     * Time of occurrence of the event. Its format is set on the consumer's side
     */
  timestamp: PropTypes.node,
  /**
     * Describes the event that took place
     */
  event: PropTypes.node,
  /**
     * Custom actions that the user can perform from the comment's header
     */
  actions: PropTypes.oneOfType([PropTypes.node.isRequired, PropTypes.arrayOf(PropTypes.node.isRequired).isRequired]),
  /**
     * Accepts any ReactNode. Renders in a panel within the comment event.
     */
  children: PropTypes.node,
  /**
     * Custom icon that shows before the username.
     */
  eventIcon: PropTypes.oneOfType([PropTypes.oneOf(["accessibility", "addDataApp", "advancedSettingsApp", "agentApp", "aggregate", "analyzeEvent", "annotation", "apmApp", "apmTrace", "appSearchApp", "apps", "arrowDown", "arrowLeft", "arrowRight", "arrowUp", "arrowStart", "arrowEnd", "article", "asterisk", "at", "auditbeatApp", "beaker", "bell", "bellSlash", "beta", "bolt", "boxesHorizontal", "boxesVertical", "branch", "branchUser", "broom", "brush", "bug", "bullseye", "calendar", "canvasApp", "casesApp", "check", "checkInCircleFilled", "cheer", "classificationJob", "clock", "cloudDrizzle", "cloudStormy", "cloudSunny", "cluster", "codeApp", "color", "compute", "console", "consoleApp", "container", "continuityAbove", "continuityAboveBelow", "continuityBelow", "continuityWithin", "controlsHorizontal", "controlsVertical", "copy", "copyClipboard", "createAdvancedJob", "createMultiMetricJob", "createPopulationJob", "createSingleMetricJob", "cross", "crossClusterReplicationApp", "crosshairs", "currency", "cut", "dashboardApp", "dataVisualizer", "database", "desktop", "devToolsApp", "discoverApp", "discuss", "document", "documentEdit", "documentation", "documents", "dot", "dotInCircle", "doubleArrowLeft", "doubleArrowRight", "download", "editorAlignCenter", "editorAlignLeft", "editorAlignRight", "editorBold", "editorChecklist", "editorCodeBlock", "editorComment", "editorDistributeHorizontal", "editorDistributeVertical", "editorHeading", "editorItalic", "editorItemAlignBottom", "editorItemAlignCenter", "editorItemAlignLeft", "editorItemAlignMiddle", "editorItemAlignRight", "editorItemAlignTop", "editorLink", "editorOrderedList", "editorPositionBottomLeft", "editorPositionBottomRight", "editorPositionTopLeft", "editorPositionTopRight", "editorRedo", "editorStrike", "editorTable", "editorUnderline", "editorUndo", "editorUnorderedList", "email", "empty", "emsApp", "eql", "eraser", "error", "exit", "expand", "expandMini", "exportAction", "eye", "eyeClosed", "faceHappy", "faceNeutral", "faceSad", "filebeatApp", "filter", "filterExclude", "filterIgnore", "filterInclude", "filterInCircle", "flag", "fleetApp", "fold", "folderCheck", "folderClosed", "folderExclamation", "folderOpen", "frameNext", "framePrevious", "fullScreen", "fullScreenExit", "function", "gear", "gisApp", "glasses", "globe", "grab", "grabHorizontal", "graphApp", "grid", "grokApp", "heart", "heartbeatApp", "heatmap", "help", "home", "iInCircle", "image", "importAction", "indexClose", "indexEdit", "indexFlush", "indexManagementApp", "indexMapping", "indexOpen", "indexPatternApp", "indexRollupApp", "indexRuntime", "indexSettings", "indexTemporary", "infinity", "inputOutput", "inspect", "invert", "ip", "key", "keyboard", "kqlField", "kqlFunction", "kqlOperand", "kqlSelector", "kqlValue", "kubernetesNode", "kubernetesPod", "launch", "layers", "lensApp", "lettering", "lineDashed", "lineDotted", "lineSolid", "link", "list", "listAdd", "lock", "lockOpen", "logoAWS", "logoAWSMono", "logoAerospike", "logoApache", "logoAppSearch", "logoAzure", "logoAzureMono", "logoBeats", "logoBusinessAnalytics", "logoCeph", "logoCloud", "logoCloudEnterprise", "logoCode", "logoCodesandbox", "logoCouchbase", "logoDocker", "logoDropwizard", "logoElastic", "logoElasticStack", "logoElasticsearch", "logoEnterpriseSearch", "logoEtcd", "logoGCP", "logoGCPMono", "logoGithub", "logoGmail", "logoGolang", "logoGoogleG", "logoHAproxy", "logoIBM", "logoIBMMono", "logoKafka", "logoKibana", "logoKubernetes", "logoLogging", "logoLogstash", "logoMaps", "logoMemcached", "logoMetrics", "logoMongodb", "logoMySQL", "logoNginx", "logoObservability", "logoOsquery", "logoPhp", "logoPostgres", "logoPrometheus", "logoRabbitmq", "logoRedis", "logoSecurity", "logoSiteSearch", "logoSketch", "logoSlack", "logoUptime", "logoVulnerabilityManagement", "logoWebhook", "logoWindows", "logoWorkplaceSearch", "logsApp", "logstashFilter", "logstashIf", "logstashInput", "logstashOutput", "logstashQueue", "machineLearningApp", "magnet", "magnifyWithExclamation", "magnifyWithMinus", "magnifyWithPlus", "managementApp", "mapMarker", "memory", "menu", "menuDown", "menuLeft", "menuRight", "menuUp", "merge", "metricbeatApp", "metricsApp", "minimize", "minus", "minusInCircle", "minusInCircleFilled", "mobile", "monitoringApp", "moon", "namespace", "nested", "node", "notebookApp", "number", "offline", "online", "outlierDetectionJob", "package", "packetbeatApp", "pageSelect", "pagesSelect", "paperClip", "partial", "pause", "payment", "pencil", "percent", "pin", "pinFilled", "pipelineApp", "pivot", "play", "playFilled", "plus", "plusInCircle", "plusInCircleFilled", "popout", "push", "questionInCircle", "quote", "recentlyViewedApp", "refresh", "regressionJob", "reporter", "reportingApp", "returnKey", "save", "savedObjectsApp", "scale", "search", "searchProfilerApp", "securityAnalyticsApp", "securityApp", "securitySignal", "securitySignalDetected", "securitySignalResolved", "sessionViewer", "shard", "share", "snowflake", "sortAscending", "sortDescending", "sortDown", "sortLeft", "sortRight", "sortUp", "sortable", "spaces", "spacesApp", "sparkles", "sqlApp", "starEmpty", "starEmptySpace", "starFilled", "starFilledSpace", "starMinusEmpty", "starMinusFilled", "starPlusEmpty", "starPlusFilled", "stats", "stop", "stopFilled", "stopSlash", "storage", "string", "submodule", "sun", "swatchInput", "symlink", "tableDensityCompact", "tableDensityExpanded", "tableDensityNormal", "tableOfContents", "tag", "tear", "temperature", "timeline", "timelineWithArrow", "timelionApp", "timeRefresh", "timeslider", "training", "trash", "unfold", "unlink", "upgradeAssistantApp", "uptimeApp", "user", "userAvatar", "users", "usersRolesApp", "vector", "videoPlayer", "visArea", "visAreaStacked", "visBarHorizontal", "visBarHorizontalStacked", "visBarVertical", "visBarVerticalStacked", "visGauge", "visGoal", "visLine", "visMapCoordinate", "visMapRegion", "visMetric", "visPie", "visTable", "visTagCloud", "visText", "visTimelion", "visVega", "visVisualBuilder", "visualizeApp", "vulnerabilityManagementApp", "warning", "alert", "watchesApp", "wordWrap", "wordWrapDisabled", "workplaceSearchApp", "wrench", "tokenAlias", "tokenAnnotation", "tokenArray", "tokenBinary", "tokenBoolean", "tokenClass", "tokenCompletionSuggester", "tokenConstant", "tokenDate", "tokenDenseVector", "tokenElement", "tokenEnum", "tokenEnumMember", "tokenEvent", "tokenException", "tokenField", "tokenFile", "tokenFlattened", "tokenFunction", "tokenGeo", "tokenHistogram", "tokenInterface", "tokenIP", "tokenJoin", "tokenKey", "tokenKeyword", "tokenMethod", "tokenMetricCounter", "tokenMetricGauge", "tokenModule", "tokenNamespace", "tokenNested", "tokenNull", "tokenNumber", "tokenObject", "tokenOperator", "tokenPackage", "tokenParameter", "tokenPercolator", "tokenProperty", "tokenRange", "tokenRankFeature", "tokenRankFeatures", "tokenRepo", "tokenSearchType", "tokenShape", "tokenString", "tokenStruct", "tokenSymbol", "tokenTag", "tokenText", "tokenTokenCount", "tokenVariable"]).isRequired, PropTypes.string.isRequired, PropTypes.elementType.isRequired]),
  /**
     * Specify an `aria-label` for the `eventIcon`.
     * If no `aria-label` is passed we assume the icon is purely decorative.
     */
  eventIconAriaLabel: PropTypes.string,
  /**
     * Background color for the comment's header.
     */
  eventColor: PropTypes.any,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiCommentEvent.__docgenInfo = {
    tags: {},
    filePath: '/Users/briannahall/Documents/EUI/eui/src/components/comment_list/comment_event.tsx',
    description: '',
    displayName: 'EuiCommentEvent',
    methods: [],
    props: {
      username: {
        defaultValue: null,
        description: 'Author of the comment.',
        name: 'username',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: true,
        type: {
          name: 'ReactNode'
        }
      },
      timestamp: {
        defaultValue: null,
        description: "Time of occurrence of the event. Its format is set on the consumer's side",
        name: 'timestamp',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      event: {
        defaultValue: null,
        description: 'Describes the event that took place',
        name: 'event',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      actions: {
        defaultValue: null,
        description: "Custom actions that the user can perform from the comment's header",
        name: 'actions',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'ReactNode | ReactNode[]'
        }
      },
      children: {
        defaultValue: null,
        description: 'Accepts any ReactNode. Renders in a panel within the comment event.',
        name: 'children',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      eventIcon: {
        defaultValue: null,
        description: 'Custom icon that shows before the username.',
        name: 'eventIcon',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'IconType'
        }
      },
      eventIconAriaLabel: {
        defaultValue: null,
        description: 'Specify an `aria-label` for the `eventIcon`.\n' + 'If no `aria-label` is passed we assume the icon is purely decorative.',
        name: 'eventIconAriaLabel',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      eventColor: {
        defaultValue: null,
        description: "Background color for the comment's header.",
        name: 'eventColor',
        parent: {
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        },
        declarations: [{
          fileName: 'eui/src/components/comment_list/comment_event.tsx',
          name: 'EuiCommentEventProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"transparent" | "accent" | "primary" | "success" | "warning" | "danger" | "subdued" | "plain"',
          value: [{
            value: '"transparent"'
          }, {
            value: '"accent"'
          }, {
            value: '"primary"'
          }, {
            value: '"success"'
          }, {
            value: '"warning"'
          }, {
            value: '"danger"'
          }, {
            value: '"subdued"'
          }, {
            value: '"plain"'
          }]
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'eui/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiCommentEventProps', 'CommonProps']
  };
} catch (e) {}